import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Task, TaskStatus, TaskExecution } from '../types';
import { tasksApi } from '../services/api/tasks';
import { queryKeys } from '../lib/queryKeys';
import { useOptionalWebSocket } from '../contexts/WebSocketContext';

/**
 * THUB-234: trailing-edge window for collapsing an `execution_updates` burst.
 *
 * `file_ready` is broadcast ONCE PER FILE (file_processing_workflow), so a 50-file
 * inbound drop delivers 50 frames in seconds. Without this, each one would fire its
 * own authenticated `/tasks/{id}/executions` call. Matches the 400ms window
 * `useCoalescedInvalidate` uses for the same reason (THUB-182).
 */
const EXECUTION_REFRESH_COALESCE_MS = 400;

export interface TaskManagementOptions {
  taskId?: string;
  autoSubscribe?: boolean;
  onTaskUpdate?: (task: Task) => void;
  onExecutionUpdate?: (execution: TaskExecution) => void;
}

export interface TaskManagementReturn {
  /** THUB-298: epoch ms of when this query's data last arrived, for a truthful "last updated". */
  dataUpdatedAt?: number;   // optional: test mocks and other callers need not supply it
  tasks: Task[];
  loading: boolean;
  error: string | null;
  getTask: (id: string) => Task | undefined;
  refreshTasks: () => void;
  subscribeToTask: (taskId: string) => () => void;
  unsubscribeFromTask: (taskId: string) => void;
  getRunningTasks: () => Task[];
  getTasksByStatus: (status: TaskStatus) => Task[];
  getTasksByPartner: (partnerId: string) => Task[];
  executeTaskManually: (taskId: string) => Promise<TaskExecution>;
  cancelTaskExecution: (executionId: string) => Promise<TaskExecution>;
  getTaskExecutions: (taskId: string, page?: number, limit?: number) => Promise<TaskExecution[]>;
}

export const useTaskManagement = (options: TaskManagementOptions = {}): TaskManagementReturn => {
  const queryClient = useQueryClient();

  // THUB-234: reuse the app's SINGLE WebSocket connection and its existing
  // `execution_updates` topic (the same channel App.tsx and DashboardOverview use)
  // rather than opening a second socket. `subscribe` from the context is already
  // referentially stable.
  const subscribe = useOptionalWebSocket()?.subscribe;

  const { data: tasks = [], isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.tasks.all,
    queryFn: () => tasksApi.getTasks(),
  });

  const getTask = useCallback((id: string): Task | undefined => {
    return tasks.find((task: Task) => task.taskId === id);
  }, [tasks]);

  const getRunningTasks = useCallback((): Task[] => {
    return tasks.filter((task: Task) => task.lastExecution?.status === 'running');
  }, [tasks]);

  const getTasksByStatus = useCallback((status: TaskStatus): Task[] => {
    return tasks.filter((task: Task) => task.status === status);
  }, [tasks]);

  const getTasksByPartner = useCallback((partnerId: string): Task[] => {
    return tasks.filter((task: Task) => task.partnerId === partnerId);
  }, [tasks]);

  const refreshTasks = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
  }, [queryClient]);

  const executeTaskManually = useCallback(async (taskId: string): Promise<TaskExecution> => {
    const execution = await tasksApi.executeTaskManually(taskId);
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    return execution;
  }, [queryClient]);

  const cancelTaskExecution = useCallback(async (executionId: string): Promise<TaskExecution> => {
    const execution = await tasksApi.cancelTaskExecution(executionId);
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    return execution;
  }, [queryClient]);

  const getTaskExecutions = useCallback(async (taskId: string, page: number = 1, limit: number = 20): Promise<TaskExecution[]> => {
    const response = await tasksApi.getTaskExecutions(taskId, page, limit);
    return response.data;
  }, []);

  // THUB-234: keep the newest `onExecutionUpdate` in a ref so `subscribeToTask`
  // stays referentially STABLE. Consumers call it from a `useEffect` that lists it
  // as a dependency (TaskExecutionMonitor), and callers pass an inline arrow for
  // `onExecutionUpdate`, which is a new function identity on every render. Closing
  // over the callback directly would therefore re-create `subscribeToTask` each
  // render, tearing down and re-establishing the WebSocket subscription in a loop.
  const onExecutionUpdateRef = useRef(options.onExecutionUpdate);
  useEffect(() => {
    onExecutionUpdateRef.current = options.onExecutionUpdate;
  }, [options.onExecutionUpdate]);

  /**
   * THUB-234: subscribe to live execution updates for ONE task.
   *
   * ROOT CAUSE this replaces: the body was `return () => {}` — a no-op stub that
   * subscribed to nothing. `TaskExecutionMonitor` called it and destructured
   * `subscribeToTask`, so the Monitor tab believed it held a live subscription while
   * `onExecutionUpdate` was never invoked from anywhere in this hook. The advertised
   * "Real-time Progress" panel could therefore never update.
   *
   * Why this REFETCHES instead of using the frame as the new execution
   * ------------------------------------------------------------------
   * Verified against the two publishers rather than assumed. `execution_updates`
   * is emitted from exactly three call sites, and every one passes a payload of only
   * `{taskId, eventType}` (+ `filename`/`taskName`):
   *   - file_processing_workflow/index.py:2250  {'taskId', 'filename', 'eventType'}
   *   - file_pickup_workflow/index.py:890       {'taskId', 'taskName', 'eventType'}
   *   - file_pickup_workflow/index.py:996       {'taskId', 'taskName', 'eventType'}
   * `shared/websocket_utils.broadcast_execution_update` then reads `executionId`,
   * `status` and `metrics` off that dict — all ABSENT — so the frame that reaches the
   * browser carries `executionId: null`, `status: null`, `progress: {}`. It is a
   * change SIGNAL, not an execution snapshot. Treating it as one would blank the
   * status and reset the file/byte counters to zero on every push, which is worse
   * than the stale-but-correct value it replaced.
   *
   * So a frame for this task triggers an authoritative refetch of the task's latest
   * execution and hands THAT to `onExecutionUpdate`. `taskId` is the one field the
   * payload does populate, which is what makes the filter below sound.
   *
   * NOTE the casing hazard: this payload is hand-built with camelCase literals, so
   * `taskId` genuinely arrives camelCase here. That is NOT true of the sibling
   * `task_status_changes` topic, whose payload is fed from `to_dict()` (snake_case)
   * and so delivers `taskId: null` — the THUB-265 defect. Do not generalise this
   * filter to that topic without re-checking its builder.
   */
  const subscribeToTask = useCallback((taskId: string): (() => void) => {
    // Optional on purpose: the Monitor renders inside modals launched from list
    // views, and a missing WebSocketProvider must cost live updates, not blank the
    // panel with a thrown error (same rationale as ConnectionDetails/PartnerDetails).
    // The 5s poll in TaskExecutionMonitor remains the fallback either way.
    if (!subscribe || !taskId) return () => {};

    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const refresh = async () => {
      timer = null;
      try {
        const [latest] = await tasksApi.getExecutionsForTask(taskId, 1);
        // Never call back after unsubscribe — that is how a setState lands on an
        // unmounted component.
        if (!disposed && latest) {
          onExecutionUpdateRef.current?.(latest);
        }
      } catch (err) {
        // A missed live update degrades to the existing 5s poll; it must not break
        // the subscription or surface as an unhandled rejection.
        console.error('Error refreshing execution after WebSocket update:', err);
      }
    };

    const unsubscribe = subscribe('execution_updates', (data: any) => {
      // Only this task's frames. Every publisher sets taskId (see above); a frame
      // without one is not attributable, so ignoring it is correct.
      if (data?.taskId !== taskId) return;
      if (timer !== null) return; // burst already scheduled
      timer = setTimeout(refresh, EXECUTION_REFRESH_COALESCE_MS);
    });

    return () => {
      disposed = true;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      unsubscribe();
    };
  }, [subscribe]);

  // Retained for API compatibility. Unsubscribing is done via the disposer that
  // `subscribeToTask` returns (the WebSocketContext contract), so there is no
  // separate per-task registry for this to tear down.
  const unsubscribeFromTask = useCallback((_taskId: string) => {}, []);

  return {
    tasks,
    loading: isLoading,
    // THUB-298: when this data actually last arrived, so consumers can show a truthful
    // "last updated" without keeping their own timestamp in step by effect.
    dataUpdatedAt,
    error: error ? (error as Error).message : null,
    getTask,
    refreshTasks,
    subscribeToTask,
    unsubscribeFromTask,
    getRunningTasks,
    getTasksByStatus,
    getTasksByPartner,
    executeTaskManually,
    cancelTaskExecution,
    getTaskExecutions,
  };
};
